import { PageHeading } from "@via/components/page-heading";
import { useAuth } from "@via/hooks/useAuth";
import CameraReader from "@via/pages/scan/camera-reader";
import {
  lookupFailedMessage,
  messageForResult,
  landingFor,
  resolveAlias,
} from "@via/pages/scan/scan.api";
import ScanUnavailable from "@via/pages/scan/scan-unavailable";
import { isScanAvailable } from "@via/utils/scan";
import { normalizeAliasCode, type VmetaAliasResolveResponse } from "common";
import {
  type FormEvent,
  type JSX,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/** The same code again inside this window is a scanner repeat or a double Enter, not a new scan. */
export const SCAN_RESUBMIT_WINDOW_MS = 1500;

export default function ScanPage(): JSX.Element {
  const { config } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scopedProjectId = searchParams.get("project");

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [crossProject, setCrossProject] =
    useState<VmetaAliasResolveResponse | null>(null);

  // The lookup in flight, if any. Aborting it marks its answer as unwanted.
  const lookup = useRef<AbortController | null>(null);
  const promptOpen = useRef(false);
  const lastSubmitted = useRef<{ code: string; at: number } | null>(null);
  // The last code that opened nothing. A camera left pointing at its label decodes it again and
  // again; it is not looked up again until the camera decodes a different code.
  const unresolvedCode = useRef<string | null>(null);
  const isMounted = useRef(false);

  const focusInput = useCallback(() => inputRef.current?.focus(), []);
  const available = isScanAvailable(config);

  // The input keeps focus: on arrival, and again once the cross project prompt closes (the input
  // is disabled while it is open, so focusing it from the Stay handler would be lost).
  useEffect(() => {
    if (available && !crossProject) focusInput();
  }, [available, crossProject, focusInput]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Leaving the page or changing its scope abandons the lookup in flight: its answer was asked
  // for in a scope that no longer applies, so it must neither navigate nor prompt. A new scope
  // also starts with no remembered code and no message.
  useEffect(
    () => () => {
      lookup.current?.abort();
      lookup.current = null;
      promptOpen.current = false;
      lastSubmitted.current = null;
      unresolvedCode.current = null;
      setIsResolving(false);
      setCrossProject(null);
      setMessage(null);
    },
    [scopedProjectId]
  );

  const submitCode = useCallback(
    async (raw: string, fromCamera = false) => {
      if (!isMounted.current) return;
      const code = normalizeAliasCode(raw);
      if (code === null || lookup.current || promptOpen.current) return;
      if (fromCamera && code === unresolvedCode.current) return;
      const now = Date.now();
      const last = lastSubmitted.current;
      if (last && last.code === code && now - last.at < SCAN_RESUBMIT_WINDOW_MS)
        return;

      const controller = new AbortController();
      lookup.current = controller;
      lastSubmitted.current = { code, at: now };
      unresolvedCode.current = null;
      setIsResolving(true);
      setMessage(null);
      try {
        const result = await resolveAlias(code, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (result.kind === "found") {
          if (scopedProjectId && result.target.canvasId !== scopedProjectId) {
            promptOpen.current = true;
            setCrossProject(result.target);
            return;
          }
          setCameraOn(false);
          const landing = landingFor(result.target);
          navigate(landing.to, { state: landing.state });
          return;
        }
        unresolvedCode.current = code;
        setMessage(messageForResult(result, code));
      } catch {
        if (!controller.signal.aborted) setMessage(lookupFailedMessage(code));
      } finally {
        if (lookup.current === controller) {
          lookup.current = null;
          setIsResolving(false);
          setValue("");
          focusInput();
        }
      }
    },
    [focusInput, navigate, scopedProjectId]
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void submitCode(value);
  };

  const stay = (): void => {
    // The camera may still be on the same label: remember it so it is not asked about again.
    unresolvedCode.current = lastSubmitted.current?.code ?? null;
    promptOpen.current = false;
    setCrossProject(null);
  };

  const open = (): void => {
    if (!crossProject) return;
    promptOpen.current = false;
    setCameraOn(false);
    const landing = landingFor(crossProject);
    navigate(landing.to, { state: landing.state });
  };

  if (!config) return <main className="p-4" />;
  if (!available) return <ScanUnavailable />;

  return (
    <main className="p-4">
      <div className="via-page-heading-wrapper">
        <PageHeading title="Scan" />
      </div>
      <form onSubmit={onSubmit} className="mt-4 flex max-w-xl gap-2">
        <label htmlFor="scan-code" className="sr-only">
          Barcode
        </label>
        <input
          id="scan-code"
          ref={inputRef}
          autoComplete="off"
          value={value}
          onChange={event => setValue(event.target.value)}
          disabled={!!crossProject}
          placeholder="Scan a barcode or type a code, then press Enter"
          className="w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
        />
        <button
          type="submit"
          disabled={isResolving || !!crossProject}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          Look up
        </button>
      </form>
      <div className="mt-3">
        <button
          type="button"
          onClick={() => {
            setCameraError(null);
            setCameraOn(on => !on);
            focusInput();
          }}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          {cameraOn ? "Stop camera" : "Use camera"}
        </button>
      </div>
      {cameraError && (
        <p role="alert" className="mt-2 text-sm text-amber-800">
          {cameraError}
        </p>
      )}
      {cameraOn && (
        <CameraReader
          onDecode={text => void submitCode(text, true)}
          onError={text => {
            setCameraError(text);
            setCameraOn(false);
            focusInput();
          }}
        />
      )}
      {message && (
        <p role="status" className="mt-4 text-sm text-gray-800">
          {message}
        </p>
      )}
      {crossProject && (
        <div
          role="alertdialog"
          aria-labelledby="scan-cross-project"
          className="mt-4 max-w-xl rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p id="scan-cross-project">
            This is in project {crossProject.canvasName}. Open it?
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={open}
              className="rounded-md bg-indigo-600 px-3 py-1.5 font-semibold text-white hover:bg-indigo-500">
              Open
            </button>
            <button
              type="button"
              onClick={stay}
              className="rounded-md bg-white px-3 py-1.5 font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              Stay
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
